import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Chip,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';

import PlannedCareCardFormContainer from './PlannedCareCardFormContainer';
import ResolutionCategoryItem, { ResolutionCategoryItemProps } from './ResolutionCategoryItem';
import SyncedWithEhrLabel from './SyncedWithEhrLabel';
import { useAnalytics } from 'src/AnalyticsContext';
import { ResolutionType } from 'src/constants/ResolutionTypes';
import { CareTask, EhrProvider } from 'src/store/sageAdminApi';
import { pluralize } from 'src/utils/intlUtils';

interface ResolutionCategoryCareTaskDropdownProps {
    resolutionType: ResolutionType;
    careTasks: CareTask[];
    ehrProvider?: EhrProvider;
    disabled?: boolean;
    // Omit the resolutionType since it's already a prop of the dropdown and we want to force them to be the same
    resolutionCategoryItemProps: Omit<ResolutionCategoryItemProps, 'resolutionType'>;
}

const ResolutionCategoryCareTaskDropdown = ({
    resolutionType,
    careTasks,
    ehrProvider,
    disabled = false,
    resolutionCategoryItemProps,
}: ResolutionCategoryCareTaskDropdownProps) => {
    const { palette, spacing } = useTheme();
    const { trackEvent } = useAnalytics();

    const handleExpansion = (event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            trackEvent('Resolution Category Dropdown Expanded', {
                pageName: 'Alert Resolution',
                resolutionType: resolutionType.displayName,
            });
        }
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                boxShadow: 0,
                borderColor: palette.grey[100],
                opacity: disabled ? 0.4 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
            }}
        >
            <Accordion onChange={handleExpansion} disableGutters>
                <AccordionSummary
                    expandIcon={
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            size="xl"
                            color={palette.grey[950]}
                            fixedWidth
                        />
                    }
                    sx={{ py: 1 }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: 1.5,
                            pr: 1,
                        }}
                    >
                        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                            {resolutionType.icon && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: spacing(5),
                                        height: spacing(5),
                                    }}
                                >
                                    <FontAwesomeIcon
                                        color={palette.grey[950]}
                                        icon={resolutionType.icon}
                                        size="3x"
                                        fixedWidth
                                    />
                                </Box>
                            )}
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {resolutionType.displayName}
                            </Typography>
                        </Stack>
                        <Chip
                            label={pluralize(careTasks.length, 'care plan task', 'care plan tasks')}
                        />
                    </Stack>
                </AccordionSummary>
                <AccordionDetails
                    sx={{ p: 0, m: 0, pt: 2, borderTop: `1px solid ${palette.grey[100]}` }}
                >
                    <Stack direction="row" sx={{ justifyContent: 'space-between', px: 1.5 }}>
                        <Typography variant="body2">Open Tasks</Typography>
                        <SyncedWithEhrLabel ehrProvider={ehrProvider} />
                    </Stack>
                    <Stack sx={{ gap: 2, p: 2 }}>
                        {careTasks.map((careTask) => (
                            <PlannedCareCardFormContainer
                                careTask={careTask}
                                showIcon={false}
                                shouldRestrictTimeFields
                                showIncompleteButton={false}
                                key={careTask.id}
                            />
                        ))}
                    </Stack>
                    <Box sx={{ pt: 1, px: 1.5 }}>
                        <Typography variant="body2">Extra Care</Typography>
                    </Box>
                    <Box sx={{ py: 2, px: 2 }}>
                        <ResolutionCategoryItem
                            showIcon={resolutionCategoryItemProps.showIcon ?? false}
                            onSelectOption={resolutionCategoryItemProps.onSelectOption}
                            isSelected={resolutionCategoryItemProps.isSelected}
                            resolutionType={resolutionType}
                            collapseContent={resolutionCategoryItemProps.collapseContent}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Card>
    );
};

export default ResolutionCategoryCareTaskDropdown;
